import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, ArrowLeft, Clock, CheckCircle2, XCircle, Eye, MailOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  applied: { label: 'Applied', icon: Clock, color: 'text-[#D97757]', bg: 'bg-[#D97757]/10' },
  reviewing: { label: 'Reviewing', icon: Eye, color: 'text-[#047857]', bg: 'bg-[#047857]/10' },
  interview: { label: 'Interview', icon: MailOpen, color: 'text-[#047857]', bg: 'bg-[#A7F3D0]/30' },
  offered: { label: 'Offered', icon: CheckCircle2, color: 'text-[#047857]', bg: 'bg-[#047857]/20' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  withdrawn: { label: 'Withdrawn', icon: XCircle, color: 'text-[#1C2A24]/40', bg: 'bg-[#F4F1E8]' },
};

export default function ApplicationTracker() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/applications`, { withCredentials: true })
      .then(r => setApps(r.data.applications))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/applications/${appId}/status`, { status }, { withCredentials: true });
      setApps(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
    } catch (err) { console.error(err); }
  };

  const filtered = filter === 'all' ? apps : apps.filter(a => a.status === filter);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>Application Tracker</h1>
            <p className="text-[#1C2A24]/60 mt-1">{apps.length} total applications</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] rounded-full bg-white border-[#E5E0D8]" data-testid="app-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="applied">Applied</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="offered">Offered</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-lg text-[#1C2A24]/60 mb-2">No applications yet</p>
            <Link to="/jobs"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white mt-4" data-testid="browse-jobs-btn">Browse Jobs</Button></Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="applications-list">
            {filtered.map(app => {
              const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.applied;
              return (
                <div key={app.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_4px_15px_rgb(0,0,0,0.03)] flex items-center gap-4" data-testid={`app-${app.id}`}>
                  <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center shrink-0`}>
                    <cfg.icon size={18} className={cfg.color} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{app.job_title}</h3>
                    <p className="text-xs text-[#1C2A24]/50">{app.company} &middot; Applied {new Date(app.applied_at).toLocaleDateString()}</p>
                  </div>
                  <Select value={app.status} onValueChange={v => updateStatus(app.id, v)}>
                    <SelectTrigger className={`w-[130px] rounded-full text-xs h-8 ${cfg.bg} border-0 ${cfg.color}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="applied">Applied</SelectItem>
                      <SelectItem value="reviewing">Reviewing</SelectItem>
                      <SelectItem value="interview">Interview</SelectItem>
                      <SelectItem value="offered">Offered</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
